import * as React from 'react'
import moment from 'moment'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { FaIcon, faPlus, faTrash } from '@/lib/fa'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import SearchColor from '@/assets/icons_new/SearchColor.svg'
import { useQueryCurrencies } from '@/modules/shared/query-client/useQueryCurrencies'
import AdvancePaymentRowItem from './AdvancePaymentRowItem'

interface BusinessTripEditAdvancePaymentsProps {
  tripData: any
  onUpdate: (field: string, value: any) => void
  onUpdateArrayItem?: (arrayName: string, index: number, updates: any) => void
  onAddArrayItem?: (arrayName: string, newItem: any) => void
  onRemoveArrayItems?: (arrayName: string, indices: number[]) => void
  companyId: string
  validationErrors?: Record<string, any>
}

const advancePaymentsColumnKeys = [
  { titleKey: 'businessTrip.col-release-date', fallback: 'DATA WYDANIA', key: 'releaseDate', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-amount', fallback: 'KWOTA', key: 'amount', type: 'number', sort: false },
  { titleKey: 'businessTrip.col-currency', fallback: 'WALUTA', key: 'currency', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-exchange-rate', fallback: 'KURS WALUTY', key: 'exchangeRate', type: 'number', sort: false },
  { titleKey: 'businessTrip.col-exchange-rate-date', fallback: 'DATA KURSU', key: 'exchangeRateDate', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-table', fallback: 'TABELA', key: 'table', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-amount-pln', fallback: 'KWOTA PLN', key: 'plnAmount', type: 'number', sort: false },
] as const

const BusinessTripEditAdvancePayments: React.FC<BusinessTripEditAdvancePaymentsProps> = ({
  tripData,
  onUpdate,
  onUpdateArrayItem,
  onAddArrayItem,
  onRemoveArrayItems,
  companyId,
}) => {
  const { t, tSafe } = useLocalize()
  const AdvancePaymentsColumns: TableHeaderColumn[] = React.useMemo(
    () =>
      advancePaymentsColumnKeys.map(({ titleKey, fallback, ...rest }) => ({
        ...rest,
        title: tSafe(titleKey, fallback),
      })),
    [tSafe],
  )
  const [searchValue, setSearchValue] = React.useState('')
  const [selectedIds, setSelectedIds] = React.useState<number[]>([])
  const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [showSearch, setShowSearch] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const { data: currencies = [] } = useQueryCurrencies({ companyId })

  // Click outside to close search
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setSearchValue('')
        setShowSearch(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  const advancePayments = tripData?.advancePayments || []

  const filteredAdvancePayments = React.useMemo(() => {
    if (!searchValue) return advancePayments
    const search = searchValue.toLowerCase()
    return advancePayments.filter((ap: any) => {
      return (
        ap.table?.toLowerCase().includes(search) ||
        ap.currency?.code?.toLowerCase().includes(search)
      )
    })
  }, [advancePayments, searchValue])

  const getOriginalIndex = (ap: any): number => {
    const idx = advancePayments.indexOf(ap)
    if (idx !== -1) return idx
    return advancePayments.findIndex((item: any) => {
      if (item.id != null && ap.id != null && String(item.id) === String(ap.id)) return true
      if (item.lp != null && ap.lp != null && item.lp !== -1 && ap.lp !== -1 && item.lp === ap.lp) return true
      return false
    })
  }

  const toggleAll = (checked: boolean) => {
    if (checked) {
      const indices = filteredAdvancePayments
        .map((ap: any) => getOriginalIndex(ap))
        .filter((i) => i !== -1)
      setSelectedIds(indices)
    } else {
      setSelectedIds([])
    }
  }

  const toggleId = (id: number) => {
    if (id === -1) return
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const handleHeaderClick = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const handleAdd = () => {
    if (onAddArrayItem) {
      onAddArrayItem('advancePayments', {
        lp: -1,
        releaseDate: moment().format('YYYY-MM-DD'),
        amount: 0,
        currency: currencies.find((c: any) => c.code === 'PLN') || { code: 'PLN', id: null },
        exchangeRate: 1,
        exchangeRateDate: moment().format('YYYY-MM-DD'),
        table: '',
        plnAmount: 0,
        enteredManually: false,
      })
    }
  }

  const handleDelete = () => {
    if (selectedIds.length > 0) {
      const indicesToRemove = new Set(selectedIds)
      const updated = advancePayments.filter((_: any, i: number) => !indicesToRemove.has(i))
      onUpdate('advancePayments', updated)
      setSelectedIds([])
    }
  }

  const isSelectedAny = selectedIds.length > 0

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-rounded-lg tw-bg-white">
      {/* Action Bar */}
      <div className="tw-flex tw-w-full tw-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
        {/* Left side - Actions */}
        <div className="tw-flex tw-items-center tw-gap-4">
          {/* Search button with dropdown */}
          <div className="tw-relative" ref={containerRef}>
            <div
              className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-2 tw-px-1 ${
                showSearch ? 'tw-rounded-lg tw-border-primary' : 'tw-border-transparent'
              }`}
              onClick={() => setShowSearch((prev) => !prev)}
            >
              <img src={SearchColor} alt="Search Icon" />
              <p className="tw-text-sm tw-font-medium tw-text-primary">
                {t('operation.find') || 'Wyszukaj'}
              </p>
            </div>
            {showSearch && (
              <div className="tw-absolute tw-z-50 tw-mt-2 tw-w-[320px] tw-rounded-lg tw-bg-white tw-p-2 tw-shadow-md">
                <div className="tw-relative tw-w-full">
                  <input
                    autoFocus
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={t('operation.find') || 'Szukaj...'}
                    className="tw-w-full tw-rounded tw-border tw-border-neutral-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="tw-absolute tw-right-2 tw-top-1/2 tw-flex tw--translate-y-1/2 tw-items-center tw-gap-2">
                    {searchValue && (
                      <>
                        <span className="tw-text-sm tw-text-gray-500">
                          {filteredAdvancePayments.length}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSearchValue('')
                          }}
                          className="tw-text-gray-400 hover:tw-text-gray-700"
                        >
                          ✕
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Add button */}
          <div
            className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] tw-cursor-pointer"
            onClick={handleAdd}
            title={t('answer.add') || 'Dodaj'}
          >
            <FaIcon icon={faPlus} className="tw-h-4 tw-w-4 tw-text-[#2448bb]" />
            <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
              {t('answer.add') || 'Dodaj'}
            </span>
          </div>

          {/* Delete button */}
          {isSelectedAny && (
            <div
              className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] tw-cursor-pointer"
              onClick={handleDelete}
              title={t('operation.delete')}
            >
              <FaIcon icon={faTrash} className="tw-h-4 tw-w-4 tw-text-error" />
              <span className="tw-text-sm tw-font-medium tw-text-error">
                {t('operation.delete')} ({selectedIds.length})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Advance Payments table — fills remaining vertical space, scrolls inside */}
      <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto tw-border-t tw-border-base-300">
        <table className="tw-w-full tw-table-auto tw-border-collapse">
          <TableHeader
            columns={AdvancePaymentsColumns}
            fontSize="md"
            isSelectedAny={isSelectedAny}
            rounded
            sticky
            sortConfig={sortConfig}
            bolder
            disabledToggle={false}
            toggleAll={toggleAll}
            onHeaderClick={handleHeaderClick}
          />
          <tbody>
            {filteredAdvancePayments.length > 0 ? (
              filteredAdvancePayments.map((ap: any, index: number) => {
                const originalIndex = getOriginalIndex(ap)
                const isChecked = selectedIds.includes(originalIndex)

                return (
                  <AdvancePaymentRowItem
                    key={
                      originalIndex !== -1 ? originalIndex : `temp-${index}`
                    }
                    companyId={companyId}
                    onUpdateArrayItem={onUpdateArrayItem}
                    isChecked={isChecked}
                    toggleId={toggleId}
                    originalIndex={originalIndex}
                    ap={ap}
                    currencies={currencies}
                  />
                )
              })
            ) : (
              <tr>
                <td colSpan={AdvancePaymentsColumns.length + 1} className="tw-p-8 tw-text-center">
                  <p className="tw-text-content-base tw-opacity-70">
                    {tSafe('businessTrip.no-data', 'Brak zaliczek')}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default BusinessTripEditAdvancePayments
